import React from "react";
import { cx } from "./utils";

export const Badge = ({ tone = "neutral", children }) => {
  const cls =
    tone === "success"
      ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
      : tone === "warning"
        ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200"
        : tone === "danger"
          ? "bg-rose-50 text-rose-800 ring-1 ring-rose-200"
          : tone === "info"
            ? "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200"
            : "bg-slate-50 text-slate-700 ring-1 ring-slate-200";

  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        cls
      )}
    >
      {children}
    </span>
  );
};

export const Card = ({ children, className }) => (
  <div
    className={cx(
      "rounded-2xl border border-slate-200 bg-white shadow-sm",
      className
    )}
  >
    {children}
  </div>
);

export const CardHeader = ({ title, subtitle, right }) => (
  <div className="flex flex-col gap-2 border-b border-slate-100 p-5 md:flex-row md:items-center md:justify-between">
    <div>
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      {subtitle ? (
        <div className="mt-0.5 text-xs text-slate-500">{subtitle}</div>
      ) : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

export const CardBody = ({ children, className }) => (
  <div className={cx("p-5", className)}>{children}</div>
);

export const Button = ({ tone = "primary", size = "md", className, ...props }) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes =
    size === "sm"
      ? "px-3 py-2 text-sm"
      : size === "lg"
        ? "px-4 py-3 text-sm"
        : "px-4 py-2.5 text-sm";
  const tones =
    tone === "primary"
      ? "bg-indigo-600 text-white hover:bg-indigo-700"
      : tone === "secondary"
        ? "bg-slate-900 text-white hover:bg-slate-800"
        : tone === "soft"
          ? "bg-slate-50 text-slate-900 hover:bg-slate-100 ring-1 ring-slate-200"
          : tone === "danger"
            ? "bg-rose-600 text-white hover:bg-rose-700"
            : "bg-white text-slate-900 hover:bg-slate-50 ring-1 ring-slate-200";

  return <button className={cx(base, sizes, tones, className)} {...props} />;
};

export const Field = ({ label, hint, children }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between gap-3">
      <label className="text-xs font-medium text-slate-700">{label}</label>
      {hint ? <span className="text-[11px] text-slate-500">{hint}</span> : null}
    </div>
    {children}
  </div>
);

export const TextInput = (props) => (
  <input
    {...props}
    className={cx(
      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20",
      props.className
    )}
  />
);

export const Select = (props) => (
  <select
    {...props}
    className={cx(
      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20",
      props.className
    )}
  />
);

export const TextArea = (props) => (
  <textarea
    {...props}
    className={cx(
      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20",
      props.className
    )}
  />
);

export const Stepper = ({ stepIndex, steps, canGoToIndex, onStepClick, summary }) => {
  return (
    <div className="w-full">
      <div className="flex w-full items-center">
        {steps.map((s, idx) => {
          const active = idx === stepIndex;
          const allowed = canGoToIndex(idx);
          const status = summary?.[s.id];

          // Consider a step completed if we've moved past it, or it reports success.
          const completed = idx < stepIndex || status?.tone === "success";
          const circleCls = completed
            ? "bg-indigo-600 text-white"
            : active
              ? "bg-slate-900 text-white"
              : allowed
                ? "bg-slate-200 text-slate-700"
                : "bg-slate-100 text-slate-400";

          const lineCls = idx < stepIndex ? "bg-indigo-600" : "bg-slate-200";

          return (
            <React.Fragment key={s.id}>
              <button
                type="button"
                onClick={() => (allowed ? onStepClick(idx) : null)}
                disabled={!allowed}
                className={cx(
                  "group flex flex-col items-center gap-1 px-1 py-2 text-center",
                  !allowed ? "cursor-not-allowed" : "cursor-pointer"
                )}
              >
                <span
                  className={cx(
                    "inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ring-1 ring-slate-200 transition",
                    circleCls,
                    active ? "ring-indigo-200" : ""
                  )}
                >
                  {idx + 1}
                </span>
                <span
                  className={cx(
                    "hidden text-[11px] font-medium sm:block",
                    active ? "text-slate-900" : allowed ? "text-slate-700" : "text-slate-400"
                  )}
                >
                  {s.label}
                </span>
              </button>

              {idx < steps.length - 1 ? <div className={cx("h-0.5 flex-1", lineCls)} /> : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
